// cache.ts

/**
 * List of IndexedDB object stores for Wiventory.
 * Add more store names as needed.
 */
export const INDEX_DB_STORES: string[] = [
  "data", "settings", "staffs", "loans", "leaves",
  "expenditures", "expenditureCategories", "contacts",
  "partners"
];

interface UpsertItem {
  storeName: string;
  item: any;
}

/**
 * Cache class using IndexedDB for persistent client-side storage.
 * Designed for caching structured data, images, and HTML in Wiventory.
 */
export class Cache {
  private dbName: string;
  private version: number;
  private db: IDBDatabase | null;

  constructor(dbName = "chrome", version = 2) {
    this.dbName = dbName;
    this.version = version;
    this.db = null;
  }

  /**
   * Initializes the IndexedDB database and object stores.
   * Automatically creates new stores if they don't exist.
   */
  async init(stores: string[] = []): Promise<IDBDatabase> {

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        stores.forEach((storeName) => {
          if (!db.objectStoreNames.contains(storeName)) {
            //  indexedDB.deleteDatabase("wiventory");
            db.createObjectStore(storeName, { keyPath: "id" });
          }
        });
      };

      request.onsuccess = (event: Event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve(this.db);
      };

      request.onerror = (event: Event) => {
        reject("IndexedDB init error: " + (event.target as IDBOpenDBRequest).error);
      };
    });
  }

  /**
   * Get a single item from the store by query,
   * with optional exclusions (e.g. exclude current user by id).
   *
   * @template T
   * @param {string} storeName - The store/collection/table name.
   * @param {Record<string, any>} [query={}] - Key/value pairs to filter results.
   * @param {Record<string, any>} [exclude] - Optional key/value pairs to exclude.
   * @returns {Promise<T|null>} The first matching item or null if none found.
   */
  async getBy<T extends Record<string, any> = any>(
    storeName: string,
    query: Record<string, any> = {},
    exclude?: Record<string, any>
  ): Promise<T | null> {
    let allItems = await this.getAllBy<T>(storeName);

    // Apply query filters (case-insensitive)
    if (Object.keys(query).length > 0) {
      allItems = allItems.filter((item) =>
        Object.entries(query).every(([key, value]) => {
          const itemValue = item[key];
          if (typeof itemValue === "string" && typeof value === "string") {
            return itemValue.toLowerCase() === value.toLowerCase();
          }
          return itemValue === value;
        })
      );
    }

    // Apply exclusions (case-insensitive)
    if (exclude) {
      for (const [key, value] of Object.entries(exclude)) {
        allItems = allItems.filter((item) => {
          const itemValue = item[key];
          if (typeof itemValue === "string" && typeof value === "string") {
            return itemValue.toLowerCase() !== value.toLowerCase();
          }
          return itemValue !== value;
        });
      }
    }

    return allItems[0] || null;
  }

  async countBy(storeName: string, query: Record<string, any> = {}): Promise<number> {
    const allItems = await this.getAllBy(storeName, query);
    return allItems.length;
  }

  async saveOne(storeName: string, item: any): Promise<void> {
    const db = this.db || (await this.init([storeName]));
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      tx.objectStore(storeName).put(item);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async upsertOne(storeName: string, item: any): Promise<void> {
    const db = this.db || (await this.init([storeName]));

    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);

      // Use put() to insert or update
      store.put(item);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async upsertMany(items: UpsertItem[]): Promise<void> {
    // Collect all unique storeNames to open transactions
    const storeNames = Array.from(new Set(items.map((i) => i.storeName)));
    const db = this.db || (await this.init(storeNames));

    return new Promise((resolve, reject) => {
      try {
        const tx = db.transaction(storeNames, "readwrite");

        for (const { storeName, item } of items) {
          tx.objectStore(storeName).put(item);
        }

        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      } catch (err) {
        reject(err);
      }
    });
  }
  /**
   * Save or update records in multiple IndexedDB stores within one transaction.
   * All changes succeed or all fail (atomic).
   *
   * @param {Record<string, any[]>} stores - Map of store names to record arrays.
   * @returns {Promise<void>} Resolves when committed, rejects on failure.
   */
  async saveMany(stores: Record<string, any[]>): Promise<void> {
    const db = this.db || (await this.init(Object.keys(stores)));

    return new Promise((resolve, reject) => {
      const tx = db.transaction(Object.keys(stores), "readwrite");

      for (const [storeName, items] of Object.entries(stores)) {
        const store = tx.objectStore(storeName);
        (items || []).forEach((item) => store.put(item));
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // async updateBy<T = any>(
  //   storeName: string,
  //   key: string | number,
  //   updateData: Partial<T>
  // ): Promise<void> {
  //   const db = this.db || (await this.init([storeName]));
  //   const tx = db.transaction(storeName, "readwrite");
  //   const store = tx.objectStore(storeName);

  //   const existing = await new Promise<T | undefined>((resolve, reject) => {
  //     const req = store.get(key);
  //     req.onsuccess = () => resolve(req.result);
  //     req.onerror = () => reject(req.error);
  //   });

  //   if (!existing) throw new Error(`No record found with key: ${key}`);

  //   const updated = { ...existing, ...updateData };

  //   await new Promise<void>((resolve, reject) => {
  //     const req = store.put(updated);
  //     req.onsuccess = () => resolve();
  //     req.onerror = () => reject(req.error);
  //   });
  // }

  async updateManyBy<T = any>(
    storeName: string,
    keys: (string | number)[], // List of key to filter for object to update
    updateData: Partial<T>
  ): Promise<void> {
    const db = this.db || (await this.init([storeName]));
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();   // ✅ only resolve if all succeed
      tx.onerror = () => reject(tx.error);

      (async () => {
        for (const key of keys) {
          const existing: T = await new Promise((res, rej) => {
            const req = store.get(key);
            req.onsuccess = () => res(req.result);
            req.onerror = () => rej(req.error);
          });

          if (!existing) {
            tx.abort();  // ❌ abort everything if one record not found
            reject(new Error(`No record found with key: ${key}`));
            return;
          }

          const updated = { ...existing, ...updateData };
          store.put(updated); // stays in same transaction
        }
      })().catch((err) => {
        tx.abort();  // ❌ rollback on any error
        reject(err);
      });
    });
  }

  async deleteOne(storeName: string, key: string | number): Promise<void> {
    const db = this.db || (await this.init([storeName]));
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      tx.objectStore(storeName).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async clear(storeName: string): Promise<void> {
    const db = this.db || (await this.init([storeName]));
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      tx.objectStore(storeName).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async clearAll(stores: string[] = INDEX_DB_STORES): Promise<void> {
    const db = this.db || (await this.init(stores));
    for (const storeName of stores) {
      const tx = db.transaction(storeName, "readwrite");
      await new Promise<void>((resolve, reject) => {
        tx.objectStore(storeName).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    }
  }

  async getAllBy<T extends Record<string, any> = any>(
    storeName: string,
    query: Record<string, any> = {},
    sortField?: keyof T,
    order: "asc" | "desc" = "asc",
    dateRange?: { field?: keyof T; start?: Date; end?: Date }
  ): Promise<T[]> {
    const db = this.db || (await this.init([storeName]));
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const result: T[] = [];

    return new Promise((resolve, reject) => {
      const request = store.openCursor();

      request.onsuccess = (event: Event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const record = cursor.value as T;

          // Match by normal query
          const match = Object.entries(query).every(([key, value]) => record[key] === value);

          // Match by optional date range
          let dateMatch = true;
          if (dateRange?.field && (dateRange.start || dateRange.end)) {
            const recordDate = new Date(record[dateRange.field as string]);
            if (dateRange.start && recordDate < dateRange.start) dateMatch = false;
            if (dateRange.end && recordDate > dateRange.end) dateMatch = false;
          }

          if (match && dateMatch) result.push(record);

          cursor.continue();
        } else {
          // Optional sorting
          if (sortField && result.length > 0) {
            const key = sortField as string;
            if (!(key in result[0])) {
              console.warn(`Sort field "${key}" does not exist in store "${storeName}". Skipping sort.`);
            } else {
              result.sort((a, b) => {
                const valA = a[key];
                const valB = b[key];
                if (valA === valB) return 0;
                return order === "asc" ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
              });
            }
          }

          resolve(result);
        }
      };

      request.onerror = () => {
        reject(new Error(`Failed to read from store "${storeName}"`));
      };
    });
  }



  async listStores(): Promise<string[]> {
    const db = this.db || (await this.init());
    return Array.from(db.objectStoreNames);
  }

  async deleteAllStores(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      const request = indexedDB.deleteDatabase(this.dbName);

      request.onsuccess = () => {
        console.log(`✅ Database "${this.dbName}" deleted successfully.`);
        this.db = null;
        resolve();
      };

      request.onerror = (event) => {
        console.error(`Failed to delete database "${this.dbName}":`, (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };

      request.onblocked = () => {
        console.warn(`Delete blocked: please close all tabs using "${this.dbName}".`);
      };
    });
  }
}

// Singleton cache instance
const cache = new Cache();

/**
 * Initialize all required IndexedDB stores for Wiventory
 * and return the shared cache instance.
 */
export async function initAllStores(stores: string[] = INDEX_DB_STORES): Promise<Cache> {
  await cache.init(stores);
  return cache;
}